/**
 * HomeAppShowcase — Hero
 *
 * Right column is the signature: the whole mobile-money loop as three stacked,
 * connected cards. All three are visible at once — an earlier version swapped
 * one card's contents, so the story took eight seconds to read and the box sat
 * mostly empty. Now the sequence highlights each step in turn while the full
 * flow stays legible from the first frame. Drawn in markup: no screenshots.
 */
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";

/* Logos shown in the social-proof pill. Chosen for contrast against each
   other so the cluster reads clearly at 28px. */
const PROOF_LOGOS = [
  "somquiz.png",
  "helguri.webp",
  "maareye.png",
  "ossobcoffee.webp",
  "koor.png",
];

/* Top reseller apps. Each highlights in turn in the hero panel. */
const NETWORKS = ["Hormuud", "Somtel", "Somnet", "Amtel", "SomLink"];

const RESELLER_APPS = [
  {
    key: "haybad",
    name: "Haybad Mall",
    logo: "haybadmall.png",
    kind: "E-commerce + Data",
    detail: "Iibso alaab iyo xirmooyin internet ah",
    accent: "var(--accent)",
  },
  {
    key: "alislaam",
    name: "Al-Islaam Data",
    logo: "alislaam.webp",
    kind: "Data",
    detail: "Xirmooyinka Hormuud, si fudud u iibso",
    accent: "#128040",
  },
  {
    key: "qamar",
    name: "Qamar Data",
    logo: "qamar.webp",
    kind: "Data",
    detail: "Xirmooyin internet ah oo raqiis ah",
    accent: "var(--accent-purple)",
  },
];

/* Google Play triangle. Four flat wedges rather than the official gradient —
   reads correctly at 12px, where a gradient turns to mud. */
function PlayGlyph({ size = 11 }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true">
      <path d="M3.6 1.8a1 1 0 0 0-.6.9v18.6a1 1 0 0 0 .6.9l9.6-10.2z" fill="#00A0FF" />
      <path d="M3.6 22.2c.3.2.7.2 1.1 0l11.2-6.4-2.7-2.8z" fill="#FF3A44" />
      <path d="M4.7 1.8c-.4-.2-.8-.2-1.1 0l9.6 10.2 2.7-2.8z" fill="#00C853" />
      <path d="M15.9 8.9l-2.7 3.1 2.7 2.8 3.5-2a1.3 1.3 0 0 0 0-2.2z" fill="#FFBC00" />
    </svg>
  );
}

function AppRow({ app, active }) {
  return (
    <motion.div
      animate={{
        opacity: active ? 1 : 0.5,
        scale: active ? 1 : 0.98,
      }}
      transition={{ duration: 0.45, ease: "easeOut" }}
      className="flex items-center gap-3.5 px-4 py-3.5"
      style={{
        background: "var(--bg-card)",
        border: `1px solid ${active ? "rgba(79, 125, 245, 0.35)" : "var(--border)"}`,
        borderRadius: "var(--radius-md)",
        boxShadow: active ? "var(--shadow-card-hover)" : "var(--shadow-card)",
      }}
    >
      {app.logo ? (
        <img
          src={`${process.env.PUBLIC_URL}/images/partners/${app.logo}`}
          alt=""
          loading="lazy"
          className="h-11 w-11 shrink-0 object-cover"
          style={{ borderRadius: 12, border: "1px solid var(--border)" }}
        />
      ) : (
        <span
          className="flex h-11 w-11 shrink-0 items-center justify-center font-heading text-base font-black text-white"
          style={{ background: app.accent, borderRadius: 12 }}
        >
          {app.name.charAt(0)}
        </span>
      )}

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="truncate text-[13px] font-bold text-navy">{app.name}</p>
          <span
            className="shrink-0 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider"
            style={{ background: "var(--badge-bg)", color: "var(--text-muted)", borderRadius: 6 }}
          >
            {app.kind}
          </span>
        </div>
        <p className="mt-0.5 truncate text-[11px]" style={{ color: "var(--text-muted)" }}>
          {app.detail}
        </p>
      </div>

      <span
        className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap px-2 py-1 text-[10px] font-semibold"
        style={{ background: "var(--badge-bg)", color: "var(--text-body)", borderRadius: 8 }}
      >
        <PlayGlyph />
        Play
      </span>
    </motion.div>
  );
}

function ResellerApps() {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setActive((n) => (n + 1) % RESELLER_APPS.length), 2400);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="mx-auto w-full" style={{ maxWidth: 420 }}>
      <div className="flex flex-col gap-3">
        {RESELLER_APPS.map((app, i) => (
          <AppRow key={app.key} app={app} active={active === i} />
        ))}
      </div>

      {/* Combined total — these three together, not each. */}
      <div
        className="mt-3 flex items-center justify-center gap-2 py-2.5"
        style={{ background: "var(--badge-bg)", borderRadius: "var(--radius-md)" }}
      >
        <PlayGlyph size={13} />
        <span className="text-[12px]" style={{ color: "var(--text-body)" }}>
          <span className="font-bold text-navy">100K+</span> soo dejin oo isku jira
        </span>
      </div>

      {/* Networks the reseller platform covers */}
      <div className="mt-4 flex flex-wrap items-center justify-center gap-1.5">
        <span className="mr-1 text-[10px] font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>
          Shabakadaha
        </span>
        {NETWORKS.map((n) => (
          <span
            key={n}
            className="px-2 py-0.5 text-[10px] font-semibold"
            style={{ borderRadius: 999, background: "var(--badge-bg)", color: "var(--text-body)" }}
          >
            {n}
          </span>
        ))}
      </div>
    </div>
  );
}

function scrollTo(id) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
}

export default function HomeAppShowcase() {
  return (
    <section className="relative overflow-hidden">
      {/* Social proof sits outside the two-column grid so it centres on the
          page, not inside the left column. */}
      <div className="relative z-10 mx-auto flex max-w-[1080px] justify-center px-6 pt-32 sm:pt-36">
        <motion.button
          onClick={() => scrollTo("projects")}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="group inline-flex items-center gap-3 py-1.5 pl-1.5 pr-4 transition-all duration-200 hover:-translate-y-0.5"
          style={{
            background: "var(--bg-card)",
            border: "1px solid var(--border)",
            borderRadius: 999,
            boxShadow: "var(--shadow-card)",
          }}
        >
          <span className="flex items-center">
            {PROOF_LOGOS.map((logo, i) => (
              <img
                key={logo}
                src={`${process.env.PUBLIC_URL}/images/partners/${logo}`}
                alt=""
                loading="lazy"
                className="h-7 w-7 rounded-full object-cover"
                style={{
                  background: "#fff",
                  border: "2px solid var(--bg-card)",
                  marginLeft: i === 0 ? 0 : -10,
                  zIndex: PROOF_LOGOS.length - i,
                }}
              />
            ))}
          </span>
          <span className="text-[13px] font-medium" style={{ color: "var(--text-body)" }}>
            <span className="font-bold text-navy">180+ app</span> oo aan dhisnay
          </span>
        </motion.button>
      </div>

      <div className="relative z-10 mx-auto grid max-w-[1080px] items-center gap-12 px-6 pb-16 pt-10 lg:grid-cols-[1fr_0.92fr] lg:gap-16">
        {/* ── Copy ── */}
        <div className="text-center">
          <motion.h1
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.08 }}
            className="font-heading leading-[1.1] text-navy"
            style={{ fontSize: "clamp(1.9rem, 3.6vw, 2.85rem)", letterSpacing: "-0.035em", fontWeight: 800 }}
          >
            Teknoolaji ganacsigaaga{" "}
            <span
              className="bg-clip-text text-transparent"
              style={{ backgroundImage: "linear-gradient(135deg, var(--accent), var(--accent-purple))" }}
            >
              korisa
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.08 }}
            className="mx-auto mt-4"
            style={{ fontSize: "0.975rem", lineHeight: 1.65, maxWidth: 470, color: "var(--text-body)" }}
          >
            Shirkad ICT ah oo Soomaaliyeed. Ganacsi kasta waxaan u dhisnaa app,
            website iyo nidaam lacag-bixin oo u gaar ah.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.16 }}
            className="mt-7 flex flex-col items-center justify-center gap-2.5 sm:flex-row"
          >
            <button
              onClick={() => scrollTo("cta")}
              className="inline-flex w-full items-center justify-center gap-2 px-6 py-3 text-sm font-semibold text-white transition-all duration-200 hover:-translate-y-0.5 sm:w-auto"
              style={{
                background: "var(--cta-gradient)",
                borderRadius: "var(--radius-md)",
                boxShadow: "0 6px 18px var(--cta-shadow)",
              }}
            >
              Bilow mashruucaaga
              <ArrowRight className="h-4 w-4" />
            </button>

            <button
              onClick={() => scrollTo("projects")}
              className="inline-flex w-full items-center justify-center px-6 py-3 text-sm font-semibold text-navy transition-all duration-200 hover:-translate-y-0.5 sm:w-auto"
              style={{
                background: "var(--bg-card)",
                border: "1px solid var(--border-strong)",
                borderRadius: "var(--radius-md)",
              }}
            >
              Arag shaqadeena
            </button>
          </motion.div>

        </div>

        {/* ── Signature: the reseller apps, the highest-volume product line ── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.24 }}
        >
          <p
            className="mb-4 text-center text-[10px] font-bold uppercase tracking-[0.2em]"
            style={{ color: "var(--text-muted)" }}
          >
            App-yada ugu caansan
          </p>
          <ResellerApps />
        </motion.div>
      </div>
    </section>
  );
}
